import { CheckCircle, Group, Lock, Public as PublicIcon } from '@mui/icons-material';
import { Grid, Typography, Button, alpha } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEventContext } from 'src/shared/contexts/eventContext';
import { getEventType } from 'src/shared/functions/getEventType';
import { EventTypeEnum } from 'src/shared/models/enums/eventType.enum';

interface EventTypeItem {
  name: string;
  description: string;
  eventType: EventTypeEnum;
  icon: JSX.Element;
}

const eventTypeArray: EventTypeItem[] = [
  {
    name: 'event:eventType.public',
    description: 'event:eventType.publicDescription',
    eventType: EventTypeEnum.Public,
    icon: <PublicIcon className='icon' />,
  },
  {
    name: 'event:eventType.nonPublic',
    description: 'event:eventType.nonPublicDescription',
    eventType: EventTypeEnum.NonPublic,
    icon: <Group className='icon' />,
  },
  {
    name: 'event:eventType.onlyMembers',
    description: 'event:eventType.onlyMembersDescription',
    eventType: EventTypeEnum.OnlyMembers,
    icon: <Lock className='icon' />,
  },
];

export const EventTypeSelector = () => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext();

  const { selectedEventType = undefined, setSelectedEventType } = useEventContext();

  useEffect(() => {
    if (selectedEventType === EventTypeEnum.Public) {
      setValue('publicSignUp', true);
      setValue('membersOnly', false);
    }
    if (selectedEventType === EventTypeEnum.NonPublic) {
      setValue('publicSignUp', false);
      setValue('membersOnly', false);
    }
    if (selectedEventType === EventTypeEnum.OnlyMembers) {
      setValue('publicSignUp', false);
      setValue('membersOnly', true);
    }
  }, [selectedEventType, setValue]);

  useEffect(() => {
    const publicSignUp = getValues('publicSignUp');
    const membersOnly = getValues('membersOnly');
    const eventType = getEventType(publicSignUp, membersOnly);
    setSelectedEventType(eventType);
  }, [getValues, setSelectedEventType]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {eventTypeArray.map((exp) => (
            <Grid item xs={12} md={4} key={exp.eventType}>
              <Button
                color='primary'
                variant='outlined'
                fullWidth
                className={selectedEventType === exp.eventType ? 'selected' : ''}
                sx={{
                  py: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  height: '100%',
                  backgroundColor: '#fff',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.success.light, 0.2),
                  },
                  '&.selected': {
                    borderWidth: 3,
                    backgroundColor: (theme) => alpha(theme.palette.success.light, 0.2),
                  },
                  '& .icon': {
                    width: '2rem',
                    height: '2rem',
                    opacity: 0.8,
                    mb: 1,
                  },
                }}
                onClick={() => setSelectedEventType(exp.eventType as EventTypeEnum)}
              >
                {exp.icon}
                <Typography variant='body1' component='div' sx={{ fontWeight: 400, display: 'flex', alignItems: 'center', mb: 1 }}>
                  {t(exp.name)}
                  {selectedEventType === exp.eventType && (
                    <CheckCircle sx={{ width: '1.5rem', height: '1.5rem', opacity: 1, ml: 1, color: (thm) => thm.palette.success.main }} />
                  )}
                </Typography>
                <Typography variant='body2' component='div' sx={{ fontWeight: 100, display: 'flex', alignItems: 'center', textTransform: 'none' }}>
                  {t(exp.description)}
                </Typography>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};
